import React from 'react';
import { theme } from '../../../theme';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;
  trend?: {
    value: number;
    label: string;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = theme.colors.primary,
  trend,
}) => {
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    border: `2px solid ${theme.colors.border}`,
    transition: 'all 0.2s',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: `${iconColor}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const trendStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const trendPositiveStyle: React.CSSProperties = {
    ...trendStyle,
    color: theme.colors.primary,
  };

  const trendNegativeStyle: React.CSSProperties = {
    ...trendStyle,
    color: theme.colors.danger,
  };

  const getTrendStyle = (value: number) => {
    if (value > 0) return trendPositiveStyle;
    if (value < 0) return trendNegativeStyle;
    return trendStyle;
  };

  return (
    <div
      style={cardStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${theme.colors.border}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={headerStyle}>
        <div style={titleStyle}>{title}</div>
        <div style={iconContainerStyle}>
          <Icon size={24} color={iconColor} />
        </div>
      </div>

      <div style={valueStyle}>{value}</div>

      {subtitle && <div style={subtitleStyle}>{subtitle}</div>}

      {trend && (
        <div style={getTrendStyle(trend.value)}>
          {trend.value > 0 && '+'}
          {trend.value}% {trend.label}
        </div>
      )}
    </div>
  );
};

export default StatCard;
